import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Promotion } from '@model/promotion';
import { PromotionService } from '@service/promotion/promotion.service';
import { Globals } from '@theme/utils/globals';
import { environment } from '@env/environment';
import Swal from 'sweetalert2/dist/sweetalert2.js';

interface DocumentCard {
  key: string;
  title: string;
  description: string;
  icon: string;
  route: string;
}

@Component({
  selector: 'app-promotion-documents',
  templateUrl: './promotion-documents.component.html',
  styleUrls: ['./promotion-documents.component.scss']
})
export class PromotionDocumentsComponent implements OnInit {
  promotion: Promotion;
  loading: boolean = true;
  saving: boolean = false;
  form: FormGroup;
  mentions: string[] = [];
  newMention: string = '';
  userSession = Globals.user;
  publicUrl = environment.publicUrl;

  documents: DocumentCard[] = [
    {
      key: 'fiche',
      title: 'Fiche de souscription',
      description: 'Fiche de souscription du client avec les details du bien et les modalites de paiement.',
      icon: 'fas fa-file-invoice',
      route: 'fiche'
    },
    {
      key: 'echeancier',
      title: 'Echeancier de paiement',
      description: 'Tableau d\'echeances mensuelles detaillant les paiements a effectuer.',
      icon: 'fas fa-calendar-alt',
      route: 'echeancier'
    },
    {
      key: 'contrat',
      title: 'Contrat de vente',
      description: 'Contrat de vente officiel entre le promoteur et le client.',
      icon: 'fas fa-file-contract',
      route: 'contrat'
    },
    {
      key: 'engagement',
      title: 'Engagement du client',
      description: 'Lettre d\'engagement signee par le client pour la reservation du bien.',
      icon: 'fas fa-handshake',
      route: 'engagement'
    },
    {
      key: 'reglement',
      title: 'Reglement interieur',
      description: 'Reglement interieur de la promotion immobiliere.',
      icon: 'fas fa-gavel',
      route: 'reglement'
    },
    {
      key: 'amortissement',
      title: 'Tableau d\'amortissement',
      description: 'Tableau d\'amortissement avec le detail du capital, interets et solde restant.',
      icon: 'fas fa-table',
      route: 'amortissement'
    },
    {
      key: 'etat-lieux',
      title: 'Etat des lieux',
      description: 'Proces-verbal d\'etat des lieux d\'entree ou de sortie du logement.',
      icon: 'fas fa-clipboard-check',
      route: 'etat-lieux'
    },
    {
      key: 'attestation',
      title: 'Attestation',
      description: 'Attestation de propriete ou de paiement delivree au client.',
      icon: 'fas fa-certificate',
      route: 'attestation'
    }
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private promotionService: PromotionService,
    private toastr: ToastrService
  ) {
    this.createForm();
  }

  ngOnInit(): void {
    const uuid = this.route.snapshot.params.id;
    if (uuid) {
      this.promotionService.getSingle(uuid).subscribe((res: any) => {
        this.promotion = res;
        this.loadReportConfig();
        this.loading = false;
      }, error => {
        this.toastr.error('Erreur lors du chargement de la promotion', 'Erreur');
        this.loading = false;
      });
    }
  }

  createForm(): void {
    this.form = this.fb.group({
      header: this.fb.group({
        showTotalCharges: [true],
        showMontantTotal: [true],
      }),
      tableau: this.fb.group({
        showChargesMensuelles: [false],
        showTotalTVA: [true],
        showTotalCharges: [true],
        showTotalTTC: [true],
      }),
      echeancier: this.fb.group({
        showTotal: [true],
      }),
      footer: this.fb.group({
        mentions: [[]],
      }),
    });
  }

  loadReportConfig(): void {
    if (this.promotion?.reportConfig) {
      const rc = this.promotion.reportConfig;
      this.form.patchValue({
        header: {
          showTotalCharges: rc.header?.showTotalCharges ?? true,
          showMontantTotal: rc.header?.showMontantTotal ?? true,
        },
        tableau: {
          showChargesMensuelles: rc.tableau?.showChargesMensuelles ?? false,
          showTotalTVA: rc.tableau?.showTotalTVA ?? true,
          showTotalCharges: rc.tableau?.showTotalCharges ?? true,
          showTotalTTC: rc.tableau?.showTotalTTC ?? true,
        },
        echeancier: {
          showTotal: rc.echeancier?.showTotal ?? true,
        },
        footer: {
          mentions: rc.footer?.mentions ?? [],
        },
      });
      this.mentions = rc.footer?.mentions ? [...rc.footer.mentions] : [];
    }
  }

  previewDocument(doc: DocumentCard): void {
    const agencyKey = this.userSession?.agencyKey;
    const userKey = this.userSession?.uuid;
    const folderUuid = this.promotion?.folderUuid || this.promotion?.uuid;
    const url = `${this.publicUrl}/printer/agency/folder/customer/${agencyKey}/${userKey}/${folderUuid}/${doc.route}`;
    window.open(url, '_blank');
  }

  saveConfig(): void {
    Swal.fire({
      title: 'Confirmation',
      text: 'Voulez-vous enregistrer la configuration du rapport PDF ?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Enregistrer',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.saving = true;
        const data = {
          promotion: this.promotion.uuid,
          reportConfig: this.form.value
        };
        this.promotionService.saveConfig(data).subscribe(res => {
          if (res?.status === 'success') {
            this.toastr.success('Configuration enregistree avec succes', 'Succes');
            if (res?.data?.reportConfig) {
              this.promotion.reportConfig = res.data.reportConfig;
            }
          }
          this.saving = false;
        }, error => {
          this.toastr.error('Erreur lors de l\'enregistrement', 'Erreur');
          this.saving = false;
        });
      }
    });
  }

  addMention(): void {
    if (this.newMention && this.newMention.trim()) {
      this.mentions.push(this.newMention.trim());
      this.newMention = '';
      this.updateMentionsControl();
    }
  }

  removeMention(index: number): void {
    this.mentions.splice(index, 1);
    this.updateMentionsControl();
  }

  private updateMentionsControl(): void {
    this.form.get('footer.mentions')?.setValue([...this.mentions]);
  }

  goBack(): void {
    window.history.back();
  }
}
